/**
 * Ring sizer engine.
 *
 * Pure logic: no DOM, no Liquid, no Shopify. The UI layer in ring-sizer.js is the
 * only thing that touches the page, so this file can be dropped unchanged into a
 * theme app extension.
 *
 * Screens report CSS pixels, not millimetres, and no browser API exposes the
 * physical size of a pixel. The only reliable way to draw a circle at true
 * physical size is to have the user match an object of known dimensions against
 * the screen. Everything below exists to support that.
 */
(function () {
  if (window.RingSizerEngine) return;

  const STORAGE_KEY = 'ring-sizer:calibration:v1';

  /**
   * Objects a shopper is likely to have to hand, with their standardised sizes.
   *
   * Cards are ISO/IEC 7810 ID-1. Coin diameters come from the issuing authority:
   * the US Mint, Banco de México, the European Central Bank, the Royal Mint, the
   * Royal Canadian Mint, the Royal Australian Mint and the Swiss National Bank.
   * Coins are not interchangeable across countries — a Canadian quarter is
   * 23.88 mm against the US 24.26 mm, and mistaking one for the other shifts the
   * result by a whole ring size — so every option must be labelled by country in
   * the UI.
   *
   * Only round coins qualify. The calibration outline is a circle, and a shopper
   * matching a heptagon or a dodecagon against it aligns to the corners on one
   * try and the flats on the next, reading a different size each time.
   *
   * Larger is better: the same one-pixel misalignment is a smaller fraction of a
   * £2 than of a 5p, so each country's list runs from largest to smallest and the
   * default picks the first that fits across the screen.
   */
  const REFERENCES = {
    card: { mm: 85.6, shape: 'rect', aspect: 85.6 / 53.98 },

    'coin-us-quarter': { mm: 24.26, shape: 'circle' },
    'coin-us-nickel': { mm: 21.21, shape: 'circle' },
    'coin-us-penny': { mm: 19.05, shape: 'circle' },

    'coin-mx-10': { mm: 28.0, shape: 'circle' },
    'coin-mx-5': { mm: 25.5, shape: 'circle' },
    'coin-mx-2': { mm: 23.0, shape: 'circle' },
    'coin-mx-1': { mm: 21.0, shape: 'circle' },

    'coin-eu-2': { mm: 25.75, shape: 'circle' },
    'coin-eu-50c': { mm: 24.25, shape: 'circle' },
    'coin-eu-1': { mm: 23.25, shape: 'circle' },

    // The £1 is dodecagonal and the 50p and 20p heptagonal: excluded.
    'coin-gb-2pound': { mm: 28.4, shape: 'circle' },
    'coin-gb-10p': { mm: 24.5, shape: 'circle' },
    'coin-gb-5p': { mm: 18.0, shape: 'circle' },

    // The $1 loonie is 11-sided: excluded.
    'coin-ca-2dollar': { mm: 28.0, shape: 'circle' },
    'coin-ca-quarter': { mm: 23.88, shape: 'circle' },
    'coin-ca-dime': { mm: 18.03, shape: 'circle' },

    // The 50c is dodecagonal: excluded.
    'coin-au-20c': { mm: 28.52, shape: 'circle' },
    'coin-au-1dollar': { mm: 25.0, shape: 'circle' },
    'coin-au-10c': { mm: 23.6, shape: 'circle' },
    'coin-au-5c': { mm: 19.41, shape: 'circle' },

    'coin-ch-5fr': { mm: 31.45, shape: 'circle' },
    'coin-ch-2fr': { mm: 27.4, shape: 'circle' },
    'coin-ch-1fr': { mm: 23.2, shape: 'circle' },
    'coin-ch-half': { mm: 18.2, shape: 'circle' }
  };

  /**
   * Inside diameter in mm for each US ring size. Standard jeweller's chart.
   *
   * Whole sizes only, because that is what the store's ring variants are.
   * Pointing a shopper at a half size we don't stock is worse than pointing them
   * at the neighbouring size we do. Half sizes sit halfway between these rows
   * (size 8½ is 18.5 mm) if the catalogue ever carries them.
   */
  const US_TABLE = [
    [3, 14.0], [4, 14.8], [5, 15.6], [6, 16.5], [7, 17.3], [8, 18.1],
    [9, 18.9], [10, 19.8], [11, 20.6], [12, 21.4], [13, 22.2]
  ];

  const SIZE_SYSTEMS = ['us', 'eu', 'es'];

  /**
   * A calibration this far outside the range of real screens means the shopper
   * matched the wrong object or mistyped, so we discard it rather than draw a
   * confidently wrong circle. Phones sit near 6 px/mm, desktops near 3.8.
   */
  const PLAUSIBLE_PX_PER_MM = { min: 2, max: 14 };

  const circumferenceMm = (diameterMm) => diameterMm * Math.PI;

  function nearestRow(table, diameterMm) {
    return table.reduce((best, row) => (
      Math.abs(row[1] - diameterMm) < Math.abs(best[1] - diameterMm) ? row : best
    ));
  }

  /**
   * How each country turns a physical ring into a number on a label.
   *
   * Spain, France and Italy subtract 40 from the inside circumference in mm, in
   * whole steps: a 54 mm circumference is a size 14. ISO 8653 uses that
   * circumference directly, also in 1 mm steps. The US system follows no formula
   * at all, so it needs the lookup table.
   *
   * `stepMm` is the distance between neighbouring sizes measured as diameter,
   * which is what the shopper is actually adjusting on screen.
   */
  const CIRCUMFERENCE_SYSTEMS = {
    es: { offset: 40 },
    eu: { offset: 0 }
  };

  /**
   * @param {string} system - 'us', 'eu' or 'es'.
   */
  function specFor(system) {
    if (system === 'us') {
      const indexOf = (size) => US_TABLE.findIndex(([entry]) => entry === size);

      return {
        sizeFor: (diameterMm) => nearestRow(US_TABLE, diameterMm)[0],
        diameterFor: (size) => US_TABLE[indexOf(size)][1],
        nextSize: (size) => {
          const index = indexOf(size);
          return index < US_TABLE.length - 1 ? US_TABLE[index + 1][0] : size;
        },
        // US sizes are not evenly spaced: 5 to 6 is 0.9 mm while 6 to 7 is 0.8.
        // Measure the real gap rather than assume an average one.
        stepAt: (size) => {
          const index = indexOf(size);
          const neighbour = index < US_TABLE.length - 1 ? US_TABLE[index + 1] : US_TABLE[index - 1];
          return Math.abs(neighbour[1] - US_TABLE[index][1]);
        }
      };
    }

    const { offset } = CIRCUMFERENCE_SYSTEMS[system] || CIRCUMFERENCE_SYSTEMS.es;
    return {
      sizeFor: (diameterMm) => Math.round(circumferenceMm(diameterMm) - offset),
      diameterFor: (size) => (size + offset) / Math.PI,
      nextSize: (size) => size + 1,
      // One millimetre of circumference, expressed as diameter.
      stepAt: () => 1 / Math.PI
    };
  }

  /**
   * The size to actually put in front of the shopper.
   *
   * A measurement can never be further than half a step from the nearest size.
   * Past 35% of a step the fit is poor enough that the shopper should be told,
   * and when the ring sits above that size we hand them the next one up: a ring
   * has to pass the knuckle, so erring large is recoverable and erring small is
   * not. The recommendation and the warning therefore always agree.
   */
  function recommend(system, diameterMm) {
    const spec = specFor(system);
    const nearest = spec.sizeFor(diameterMm);
    const canonicalDiameterMm = spec.diameterFor(nearest);
    const isBetweenSizes = Math.abs(diameterMm - canonicalDiameterMm) > spec.stepAt(nearest) * 0.35;
    const size = isBetweenSizes && canonicalDiameterMm < diameterMm ? spec.nextSize(nearest) : nearest;

    return { size, label: String(size), isBetweenSizes };
  }

  /** The same ring expressed in every other system, for shoppers abroad. */
  function equivalents(system, diameterMm) {
    return SIZE_SYSTEMS
      .filter((candidate) => candidate !== system)
      .map((candidate) => ({ system: candidate, label: recommend(candidate, diameterMm).label }));
  }

  /** Everything the UI needs to render one measurement. */
  function describe(system, diameterMm) {
    const { label, isBetweenSizes } = recommend(system, diameterMm);

    return {
      label,
      diameterMm,
      circumferenceMm: circumferenceMm(diameterMm),
      isBetweenSizes,
      equivalents: equivalents(system, diameterMm)
    };
  }

  /**
   * Physical pixel density of Apple's screens, keyed by native resolution.
   * Values are the published Apple specs; the key is the panel's own pixel count,
   * which is a property of the hardware and does not move with browser zoom.
   *
   * Only Apple is listed. Its hardware is a closed, enumerable set, whereas
   * Android ships the same resolution at wildly different densities — a
   * 1080x1920 panel spans roughly 400 to 580 ppi across vendors, and picking
   * wrong there would hand the shopper a confidently incorrect ring size.
   * Everyone else calibrates.
   */
  const APPLE_PPI = {
    '320x480': 163, // original, 3G, 3GS
    '640x960': 326, // 4, 4S
    '640x1136': 326, // 5, 5S, SE (1st) — unreachable: ambiguous, see below
    '750x1334': 326, // 6, 6S, 7, 8, SE (2nd, 3rd)
    '1080x1920': 401, // 6 Plus, 6S Plus, 7 Plus, 8 Plus
    '828x1792': 326, // XR, 11
    '1125x2436': 458, // X, XS, 11 Pro — unreachable: ambiguous, see below
    '1242x2688': 458, // XS Max, 11 Pro Max — unreachable: ambiguous, see below
    '1080x2340': 476, // 12 mini, 13 mini
    '1170x2532': 460, // 12, 12 Pro, 13, 13 Pro, 14
    '1284x2778': 458, // 12 Pro Max, 13 Pro Max, 14 Plus
    '1179x2556': 460, // 14 Pro, 15, 15 Pro, 16
    '1290x2796': 460, // 14 Pro Max, 15 Plus, 15 Pro Max, 16 Plus
    '1206x2622': 460, // 16 Pro, 17, 17 Pro
    '1260x2736': 460, // Air
    '1320x2868': 460, // 16 Pro Max, 17 Pro Max

    '1620x2160': 264, // iPad 10.2"
    '1640x2360': 264, // iPad Air 10.9", iPad (10th)
    '1668x2224': 264, // iPad Air 10.5", iPad Pro 10.5"
    '1668x2388': 264, // iPad Pro 11"
    '1668x2420': 264, // iPad Pro 11" (M4)
    '2048x2732': 264, // iPad Pro 12.9"
    '2064x2752': 264, // iPad Pro 13" (M4)
    '1488x2266': 326 // iPad mini (6th, 7th)
  };

  /**
   * Resolutions shared by devices of different densities. The 9.7" iPad is
   * 264 ppi and the iPad mini 4 and 5 are 326 ppi, and nothing the browser
   * exposes tells them apart: Safari never puts a model name in the user-agent,
   * and modern iPadOS reports itself as a Mac. A 23% error is up to five ring
   * sizes, so these calibrate rather than guess.
   *
   * The iPhone entries are Display Zoom collisions. A zoomed iPhone renders a
   * smaller logical buffer and stretches it across the panel: a zoomed 17
   * draws 1125x2436 — the native buffer of an iPhone X — while its CSS pixels
   * are ~7% larger than an X's, a full ring size. The buffer is all the
   * browser exposes, so both devices must calibrate: the real X pays one extra
   * tap so the zoomed 17 can't be handed a confidently wrong size. (The 5-era
   * key collides the same way despite equal ppi — the zoomed 8 stretches
   * 640x1136 over a 750-pixel panel, so its CSS pixels are 17% larger.)
   */
  const AMBIGUOUS_RESOLUTIONS = new Set([
    '1536x2048', // iPad 9.7" vs iPad mini 4/5
    '1125x2436', // X, XS, 11 Pro vs zoomed 12–17 (non-Max)
    '1242x2688', // XS Max, 11 Pro Max vs zoomed Max models
    '640x1136' // 5, 5S, SE (1st) vs zoomed 6–8, SE (2nd, 3rd)
  ]);

  /**
   * Best guess for the ambiguous panels above, so nobody is marched through
   * calibration before being allowed to measure. Both candidate densities are
   * known for each key, so the midpoint caps the error at ~3% — a third of a
   * ring size — where trusting either candidate outright risks a full size.
   * The 5-era key assumes the zoomed modern phone instead: the 326 ppi
   * originals predate 2016 and have effectively left circulation.
   *
   * Every value here ships with the "approximate" warning in the UI. It is a
   * starting point the shopper can refine, never an answer presented as exact.
   */
  const AMBIGUOUS_PX_PER_MM = {
    '1125x2436': 5.82, // iPhone X family 6.01 vs zoomed 12–17 (5.63–5.80)
    '1242x2688': 5.84, // XS Max family 6.01 vs zoomed Max models (5.68–5.81)
    '640x1136': 5.48, // zoomed 6–8/SE; the real 5/5S/SE (6.42) are museum pieces
    '1536x2048': 5.81 // iPad 9.7" (5.20) vs iPad mini 4/5 (6.42) — widest spread of all
  };

  /** iPadOS reports itself as a Mac, and only its touch support gives it away. */
  function isAppleDevice() {
    const ua = navigator.userAgent;
    return /iPhone|iPad|iPod/i.test(ua)
      || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  }

  /**
   * A ring has to be laid flat against the screen. That works on a device you can
   * pick up and tilt; against an upright monitor it is guesswork.
   *
   * The width test is only a last resort: a 13" iPad is 1032 CSS pixels wide and
   * calls itself a Mac, yet it is the best surface of all to rest a ring on.
   * Touch support, not size, is what says the screen can be picked up.
   */
  function isHandheld() {
    if (/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)) return true;
    if (isAppleDevice()) return true;

    return 'ontouchstart' in window && window.screen.width <= 1024;
  }

  /**
   * The panel's native resolution, orientation-independent. Unlike CSS pixels,
   * this is fixed hardware: browser zoom shrinks screen.width and grows
   * devicePixelRatio in step, leaving the product unchanged.
   */
  function devicePixelsKey() {
    const dpr = window.devicePixelRatio || 1;
    const width = Math.round(window.screen.width * dpr);
    const height = Math.round(window.screen.height * dpr);

    return `${Math.min(width, height)}x${Math.max(width, height)}`;
  }

  /**
   * Pixels per CSS millimetre for a device we can identify with certainty, so the
   * shopper can skip calibration entirely and just lay their ring on the glass.
   * Returns null whenever there is any doubt — a wrong answer here is invisible
   * to the shopper, which makes it far worse than one more tap.
   */
  function recognisedPxPerMm() {
    if (!isAppleDevice()) return null;

    const key = devicePixelsKey();
    if (AMBIGUOUS_RESOLUTIONS.has(key)) return null;

    const ppi = APPLE_PPI[key];
    if (!ppi) return null;

    const pxPerMm = ppi / ((window.devicePixelRatio || 1) * 25.4);
    return isPlausible(pxPerMm) ? pxPerMm : null;
  }

  /**
   * Guesses pixel density before the shopper has calibrated, purely so the first
   * frame is roughly the right size rather than absurd. Never trusted for a
   * final measurement.
   */
  function estimatePxPerMm() {
    const dpr = window.devicePixelRatio || 1;

    let cssPpi;
    if (isHandheld()) cssPpi = dpr >= 3 ? 160 : 150;
    else cssPpi = dpr >= 2 ? 120 : 96;

    return cssPpi / 25.4;
  }

  /**
   * The density to measure with when nothing better exists: the compromise for
   * a known-ambiguous panel, or the class-of-device estimate. Callers must pair
   * it with a visible warning and a route into calibration — it exists to let
   * the shopper start measuring immediately, not to claim the screen is known.
   */
  function fallbackPxPerMm() {
    if (isAppleDevice()) {
      const compromise = AMBIGUOUS_PX_PER_MM[devicePixelsKey()];
      if (compromise) return compromise;
    }
    return estimatePxPerMm();
  }

  /**
   * Identifies the screen a calibration was taken on. Browser zoom changes
   * devicePixelRatio, which invalidates the key and forces a recalibration —
   * that is correct, because zooming really does change how big a CSS pixel is.
   */
  function deviceKey() {
    return `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio || 1}`;
  }

  function isPlausible(pxPerMm) {
    return Number.isFinite(pxPerMm)
      && pxPerMm > PLAUSIBLE_PX_PER_MM.min
      && pxPerMm < PLAUSIBLE_PX_PER_MM.max;
  }

  function loadCalibration() {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (!saved || saved.key !== deviceKey() || !isPlausible(saved.pxPerMm)) return null;
      return saved.pxPerMm;
    } catch (error) {
      return null;
    }
  }

  function saveCalibration(pxPerMm) {
    if (!isPlausible(pxPerMm)) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ pxPerMm, key: deviceKey() }));
    } catch (error) {
      // Private browsing blocks writes. The calibration still holds for this visit.
    }
  }

  function clearCalibration() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Nothing to recover from: the shopper is about to recalibrate anyway.
    }
  }

  /**
   * Picks the reference object that gives the most precision while still fitting
   * across the screen. A card is 85.6 mm wide and a phone screen is about 71 mm,
   * so cards are desktop-only and coins carry mobile. Among coins, bigger spans
   * more pixels and so measures more finely.
   *
   * @param {string[]} availableIds - Reference ids offered to this shopper.
   * @param {number} viewportPx - Usable width in CSS pixels.
   */
  function defaultReference(availableIds, viewportPx) {
    const estimate = estimatePxPerMm();
    const known = availableIds.filter((id) => REFERENCES[id]);
    const fits = (id) => REFERENCES[id].mm * estimate <= viewportPx;

    if (known.includes('card') && fits('card')) return 'card';

    const coins = known
      .filter((id) => id !== 'card')
      .sort((a, b) => REFERENCES[b].mm - REFERENCES[a].mm);

    return coins.find(fits) || coins[coins.length - 1] || 'card';
  }

  window.RingSizerEngine = {
    REFERENCES,
    US_TABLE,
    SIZE_SYSTEMS,
    APPLE_PPI,
    AMBIGUOUS_RESOLUTIONS,
    isAppleDevice,
    devicePixelsKey,
    recognisedPxPerMm,
    circumferenceMm,
    describe,
    recommend,
    equivalents,
    isHandheld,
    estimatePxPerMm,
    fallbackPxPerMm,
    deviceKey,
    isPlausible,
    loadCalibration,
    saveCalibration,
    clearCalibration,
    defaultReference
  };
}());
